import { useEffect, useRef, useState } from 'react';
import { OtpChallengeError } from '@tark/shared/lib';
import type { OtpRequestResponse } from '@tark/shared/types';

const seconds = (value: number | undefined, fallback: number) =>
  Number.isFinite(value) && value! >= 0 ? Math.min(86400, value!) : fallback;

const WEB_SIGN_IN_DENIED = 'Sign in with the TARK phone app first. Web access opens once your phone and email are verified.';

export function useOtpRecovery() {
  const [otp, setOtp] = useState('');
  const [sessionToken, setSessionToken] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [expiresAt, setExpiresAt] = useState(0);
  const [retryAt, setRetryAt] = useState(0);
  const [verifyAfter, setVerifyAfter] = useState(0);
  const [now, setNow] = useState(Date.now);
  const inFlight = useRef(false);
  const retryDeadline = useRef(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    const tick = () => setNow(Date.now());
    const timer = window.setInterval(tick, 1000);
    window.addEventListener('focus', tick);
    document.addEventListener('visibilitychange', tick);
    return () => {
      mounted.current = false;
      window.clearInterval(timer);
      window.removeEventListener('focus', tick);
      document.removeEventListener('visibilitychange', tick);
    };
  }, []);

  const waitUntil = (deadline: number) => {
    retryDeadline.current = Math.max(retryDeadline.current, deadline);
    setRetryAt(retryDeadline.current);
    setNow(Date.now());
  };
  const clearChallenge = () => {
    if (inFlight.current) return;
    setOtp('');
    setSessionToken('');
    setError('');
    setNotice('');
    setExpiresAt(0);
  };
  const requestCode = async (send: () => Promise<OtpRequestResponse>) => {
    if (inFlight.current || Date.now() < retryDeadline.current) return null;
    inFlight.current = true;
    setBusy(true);
    setError('');
    setNotice('');
    const startedAt = Date.now();
    waitUntil(startedAt + 60000);
    try {
      const result = await send();
      if (!mounted.current) return null;
      setSessionToken(result.sessionToken);
      setOtp('');
      setVerifyAfter(0);
      setExpiresAt(startedAt + seconds(result.expiresIn, 300) * 1000);
      waitUntil(Date.now() + seconds(result.retryAfterSeconds, 60) * 1000);
      setNotice('Code sent. Enter the latest code you received.');
      return result;
    } catch (failure) {
      if (!mounted.current) return null;
      if (failure instanceof OtpChallengeError && failure.code === 'WEB_SIGN_IN_REQUIRES_MOBILE_ACCOUNT') {
        setError(WEB_SIGN_IN_DENIED);
      } else if (failure instanceof OtpChallengeError && failure.code === 'RATE_LIMITED') {
        waitUntil(Date.now() + seconds(failure.retryAfterSeconds, 60) * 1000);
        setError('Too many code requests. Please wait before requesting another code.');
      } else {
        setError('We could not confirm that a code was sent. Check your messages, then try again when available.');
      }
      return null;
    } finally {
      inFlight.current = false;
      if (mounted.current) setBusy(false);
    }
  };
  const verifyCode = async <T,>(verify: () => Promise<T>): Promise<T | null> => {
    if (inFlight.current || !sessionToken || otp.length !== 6 || Date.now() < verifyAfter) return null;
    if (expiresAt && Date.now() >= expiresAt) {
      setError('That code has expired. Request a new code.');
      return null;
    }
    inFlight.current = true;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      return await verify();
    } catch (failure) {
      if (!mounted.current) return null;
      if (failure instanceof OtpChallengeError) {
        if (failure.sessionToken) setSessionToken(failure.sessionToken);
        if (failure.code === 'WEB_SIGN_IN_REQUIRES_MOBILE_ACCOUNT') {
          setError(WEB_SIGN_IN_DENIED);
        } else if (failure.code === 'OTP_EXPIRED' || failure.code === 'OTP_SESSION_ENDED') {
          setSessionToken('');
          setError(failure.code === 'OTP_EXPIRED'
            ? 'That code has expired. Request a new code.'
            : 'This verification session has ended. Request a new code.');
        } else if (failure.code === 'RATE_LIMITED') {
          const deadline = Date.now() + seconds(failure.retryAfterSeconds, 60) * 1000;
          waitUntil(deadline);
          setVerifyAfter(deadline);
          setError('Too many attempts. Please wait before trying again.');
        } else if (failure.code === 'OTP_INCORRECT' || /invalid otp|did not match|incorrect code/i.test(failure.message)) {
          setError('That code did not match. Check it and try again.');
        } else if (/expired/i.test(failure.message)) {
          setSessionToken('');
          setError('That code has expired. Request a new code.');
        } else {
          setError('We could not verify the code right now. Check your connection and try again.');
        }
      } else {
        setError('We could not verify the code right now. Check your connection and try again.');
      }
      return null;
    } finally {
      inFlight.current = false;
      if (mounted.current) setBusy(false);
    }
  };
  const expired = expiresAt > 0 && now >= expiresAt;
  useEffect(() => {
    if (expired) setError('That code has expired. Request a new code.');
  }, [expired]);
  return {
    otp, setOtp, sessionToken, setError, notice: expired ? '' : notice, busy, expired,
    verificationBlocked: now < verifyAfter,
    error,
    resendIn: Math.max(0, Math.ceil((retryAt - now) / 1000)),
    requestCode, verifyCode, clearChallenge,
  };
}
